import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { X, GitCompare, ShieldAlert, Check, Loader2 } from 'lucide-react';
import {
  resolveVersionGenerationJob,
  type ComplianceResolveDecision,
  type VersionGenerationJob,
} from '@/api/agentVersionGeneration';

interface VersionPromptModalProps {
  job: VersionGenerationJob | null;
  onClose: () => void;
  /**
   * Called with the saved job after a compliance-blocked save is resolved, so
   * the editor can refresh its version list / clear the unsaved-changes state.
   */
  onResolved?: (job: VersionGenerationJob) => void;
}

/**
 * Read-only viewer for a freshly-generated version's system prompt.
 *
 * When the generation was blocked by compliance this is also the *recovery*
 * surface: a flagged save is a gate, not a dead end. The admin can apply the
 * compliance agent's suggested rewrite or acknowledge the flag and save the
 * prompt as written — mirroring the Slack compliance gate. Without these the
 * editor hard-locks and the draft is lost.
 */
export function VersionPromptModal({ job, onClose, onResolved }: VersionPromptModalProps) {
  const [resolving, setResolving] = useState<ComplianceResolveDecision | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // This component never unmounts — the provider always renders it and it
  // returns null when there is no job — so per-job state must be reset
  // explicitly. Without this, a completed resolve leaves `resolving` set and the
  // next time the modal opens it is stuck busy: close button disabled and
  // backdrop click ignored.
  const jobId = job?.id ?? null;
  useEffect(() => {
    setResolving(null);
    setResolveError(null);
  }, [jobId]);

  if (!job) return null;

  const isBlocked = job.status === 'blocked';
  const versionLabel = job.version_label ? `v${job.version_label}` : 'new version';
  const busy = resolving !== null;

  // The backend decides what is permitted for this verdict — BLOCK returns only
  // `save_as_draft`, everything else allows proceeding. Falling back to
  // inference keeps jobs created before `allowed_decisions` existed usable.
  const allowed = new Set<ComplianceResolveDecision>(
    job.compliance?.allowed_decisions?.length
      ? job.compliance.allowed_decisions
      : ([
          'override_submit',
          ...(job.compliance?.amendment ? (['accept_changes'] as const) : []),
        ] as ComplianceResolveDecision[])
  );
  const flaggedSections = job.compliance?.flagged_sections ?? [];
  const isHardBlock = allowed.has('save_as_draft');

  const handleResolve = async (decision: ComplianceResolveDecision) => {
    setResolving(decision);
    setResolveError(null);
    try {
      const saved = await resolveVersionGenerationJob(job.agent_id, job.id, decision);
      onResolved?.(saved);
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setResolveError(detail || 'Could not save the version. Please try again.');
    } finally {
      // Always clear the busy flag — on success too. The component stays
      // mounted, so leaving it set would disable close on the next open.
      setResolving(null);
    }
  };

  return (
    <div data-testid="version-prompt-modal" className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-[700px] max-h-[85vh] flex flex-col">
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {job.agent_name ? `${job.agent_name} — ${versionLabel}` : `System prompt — ${versionLabel}`}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {isBlocked ? 'This version was flagged by compliance and was not saved.' : 'This version is now active.'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 ml-3 disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {isBlocked && (
          <div className="mx-6 mt-4 rounded-lg border px-4 py-3 flex items-start gap-3 bg-red-50 border-red-200 text-red-900 dark:bg-red-900/20 dark:border-red-800 dark:text-red-100">
            <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1 text-xs space-y-1">
              <p className="font-semibold">Compliance check flagged this prompt</p>
              <p className="leading-relaxed">
                {job.compliance?.rationale ||
                  'The generated prompt was flagged by our compliance check, so it was not saved. Review the flag and choose how to continue.'}
              </p>
              {flaggedSections.length > 0 && (
                <p data-testid="compliance-flagged-sections" className="leading-relaxed pt-0.5">
                  <span className="font-semibold">Needs editing:</span> {flaggedSections.join(', ')}
                </p>
              )}
              {isHardBlock && (
                <p className="leading-relaxed pt-0.5">
                  This verdict cannot be overridden. You can keep the work as a draft, but it will not go live — create
                  a new version that resolves the finding.
                </p>
              )}
            </div>
          </div>
        )}

        {job.changelog && (
          <div className="px-6 pt-4 flex-shrink-0">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 px-3 py-2">
              <div className="flex items-center gap-2 mb-1">
                <GitCompare className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">What changed</span>
              </div>
              <div className="prose prose-xs dark:prose-invert max-w-none text-xs text-gray-700 dark:text-gray-200 leading-relaxed">
                <ReactMarkdown>{job.changelog}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{job.content || '_No prompt content available._'}</ReactMarkdown>
          </div>
        </div>

        {isBlocked && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 space-y-3">
            {resolveError && (
              <p data-testid="compliance-resolve-error" className="text-xs text-red-600 dark:text-red-400">
                {resolveError}
              </p>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                data-testid="compliance-dismiss"
                onClick={onClose}
                disabled={busy}
                className="px-3 py-2 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
              >
                Keep editing
              </button>
              {allowed.has('save_as_draft') && (
                <button
                  data-testid="compliance-save-draft"
                  onClick={() => handleResolve('save_as_draft')}
                  disabled={busy}
                  title="Keep this work as a draft. It can never be activated — you must create a new, compliant version."
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800 disabled:opacity-40 flex items-center gap-1.5"
                >
                  {resolving === 'save_as_draft' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Save as draft
                </button>
              )}
              {allowed.has('override_submit') && (
                <button
                  data-testid="compliance-proceed-anyway"
                  onClick={() => handleResolve('override_submit')}
                  disabled={busy}
                  title="Save the prompt as written. The override is recorded for audit."
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/30 disabled:opacity-40 flex items-center gap-1.5"
                >
                  {resolving === 'override_submit' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Acknowledge &amp; Proceed Anyway
                </button>
              )}
              {allowed.has('accept_changes') && (
                <button
                  data-testid="compliance-apply-fix"
                  onClick={() => handleResolve('accept_changes')}
                  disabled={busy}
                  title="Save the compliance-corrected version of this prompt."
                  className="px-3 py-2 text-xs font-medium rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-40 flex items-center gap-1.5"
                >
                  {resolving === 'accept_changes' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  Apply suggested fix
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
